'use client';

import './index.css';

import { storyblokEditable } from '@storyblok/react';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { useState } from 'react';

import { MaxWidth } from '@/components/max-width';
import { Pill } from '@/components/pill';

import { hasText, render } from '@/helpers/storyblok/rich-text';
import { markdownToHtml } from '@/helpers/string';

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7 1v12M1 7h12"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const MinusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1 7h12"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const QuestionsAnswersItem = ({ item, index, isOpen, onToggle }) => {
  const hasAnswer = hasText(item.answer);

  return (
    <li
      {...storyblokEditable(item)}
      className={classnames('qa__item', {
        'qa__item--open': isOpen,
        'qa__item--static': !hasAnswer,
      })}
    >
      {hasAnswer ? (
        <button
          type="button"
          className="qa__item__header"
          onClick={() => onToggle(index)}
          aria-expanded={isOpen}
        >
          <p className="qa__item__question">{item.question}</p>
          <span className="qa__item__icon" aria-hidden="true">
            {isOpen ? <MinusIcon /> : <PlusIcon />}
          </span>
        </button>
      ) : (
        <div className="qa__item__header">
          <p className="qa__item__question">{item.question}</p>
        </div>
      )}

      {hasAnswer ? (
        <motion.div
          className="qa__item__answer"
          initial={false}
          animate={{ height: isOpen ? 'auto' : 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <div className="qa__item__answer__inner">{render(item.answer)}</div>
        </motion.div>
      ) : null}
    </li>
  );
};

export const QuestionsAnswers = ({ blok }) => {
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = (index) => {
    setOpenIndex((current) => (current === index ? null : index));
  };

  const TitleEl = blok.titleLevel || 'h2';
  const items = blok.items || [];
  const hasHeader = Boolean(blok.pill || blok.title);

  return (
    <div {...storyblokEditable(blok)} className="qa">
      <MaxWidth>
        <div className="qa__container">
          {hasHeader ? (
            <div className="qa__header">
              <Pill>{blok.pill}</Pill>

              {blok.title ? (
                <TitleEl
                  className="qa__title"
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(blok.title),
                  }}
                />
              ) : null}
            </div>
          ) : null}

          <ul className="qa__list">
            {items.map((item, index) => (
              <QuestionsAnswersItem
                key={item._uid}
                item={item}
                index={index}
                isOpen={openIndex === index}
                onToggle={handleToggle}
              />
            ))}
          </ul>
        </div>
      </MaxWidth>
    </div>
  );
};
